import { useEffect, useState } from "react";

interface VideoScrollEffectOptions {
  startOffset?: number;
  endOffset?: number;
  minWidth?: number;
  maxBorderRadius?: number;
}

interface VideoScrollEffect {
  width: number;
  borderRadius: number;
}

export const useVideoScrollEffect = (
  videoRef: React.RefObject<HTMLElement>,
  options: VideoScrollEffectOptions = {}
): VideoScrollEffect => {
  const {
    startOffset = 0,
    endOffset = 400,
    minWidth = 80,
    maxBorderRadius = 32,
  } = options;

  const [scrollValues, setScrollValues] = useState<VideoScrollEffect>({
    width: 100,
    borderRadius: 0,
  });

  const [effectStarted, setEffectStarted] = useState(false);
  const [maxProgressReached, setMaxProgressReached] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!videoRef.current) return;

      const rect = videoRef.current.getBoundingClientRect();
      const videoTop = rect.top;
      const videoHeight = rect.height;
      const windowHeight = window.innerHeight;

      // Calculer le pourcentage de la vidéo visible
      const visibleTop = Math.max(0, windowHeight - videoTop);
      const visibleBottom = Math.max(0, videoTop + videoHeight - 0);
      const visibleHeight = Math.min(visibleTop, visibleBottom, videoHeight);
      const visibilityPercentage = (visibleHeight / videoHeight) * 100;

      // Commencer l'effet seulement quand au moins 30% de la vidéo est visible (plus tôt)
      if (visibilityPercentage < 30 && !effectStarted) {
        setScrollValues({
          width: 100,
          borderRadius: 0,
        });
        return;
      }

      // Calculer la progression en fonction du scroll une fois que 30% est visible
      const scrollFromTop = windowHeight - videoTop;
      const adjustedStartOffset = videoHeight * 0.3 + startOffset; // Commencer quand 50% visible + offset (réduit)

      if (scrollFromTop <= adjustedStartOffset && !effectStarted) {
        // Avant le début de l'effet - uniquement si l'effet n'a jamais commencé
        setScrollValues({
          width: 100,
          borderRadius: 0,
        });
      } else if (scrollFromTop >= adjustedStartOffset + endOffset) {
        // Après la fin de l'effet - maintenir les valeurs max
        setEffectStarted(true);
        setMaxProgressReached(1);
        setScrollValues({
          width: minWidth,
          borderRadius: maxBorderRadius,
        });
      } else if (scrollFromTop > adjustedStartOffset) {
        // Pendant l'effet - interpolation linéaire
        setEffectStarted(true);
        const progress = (scrollFromTop - adjustedStartOffset) / endOffset;
        const clampedProgress = Math.max(0, Math.min(1, progress));

        // Mise à jour du progrès maximum atteint
        if (clampedProgress > maxProgressReached) {
          setMaxProgressReached(clampedProgress);
        }

        // Utiliser le progrès maximum atteint pour éviter de revenir en arrière
        const finalProgress = Math.max(clampedProgress, maxProgressReached);
        // Courbe d'animation encore plus douce (quartic-out pour une progression plus lente)
        const easeProgress = 1 - Math.pow(1 - finalProgress, 4);

        const currentWidth = 100 - (100 - minWidth) * easeProgress;
        const currentBorderRadius = maxBorderRadius * easeProgress;

        setScrollValues({
          width: currentWidth,
          borderRadius: currentBorderRadius,
        });
      } else if (effectStarted && scrollFromTop <= adjustedStartOffset) {
        // Si on remonte après avoir commencé l'effet, permettre le retour en arrière
        const progress = Math.max(
          0,
          (scrollFromTop - adjustedStartOffset) / endOffset
        );
        setMaxProgressReached(progress);

        if (progress <= 0) {
          setEffectStarted(false);
          setScrollValues({
            width: 100,
            borderRadius: 0,
          });
        } else {
          const easeProgress = 1 - Math.pow(1 - progress, 4);
          const currentWidth = 100 - (100 - minWidth) * easeProgress;
          const currentBorderRadius = maxBorderRadius * easeProgress;

          setScrollValues({
            width: currentWidth,
            borderRadius: currentBorderRadius,
          });
        }
      }
    };

    // Appel initial pour définir l'état initial
    handleScroll();

    // Ajouter l'écouteur de scroll
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [
    videoRef,
    startOffset,
    endOffset,
    minWidth,
    maxBorderRadius,
    effectStarted,
    maxProgressReached,
  ]);

  return scrollValues;
};
